import { Rand } from './rng';
import {
  BREAK_DEPTH_RATIO,
  CREST_DEPTH_RATIO,
  GRAVITY,
  REFORM_DEPTH_RATIO,
  TUNING,
} from './tuning';

/** 宝の等級と配点。 */
export type Tier = 'shell' | 'pearl' | 'coin' | 'relic';

export const TREASURE_POINTS: Record<Tier, number> = {
  shell: 10,
  pearl: 50,
  coin: 200,
  relic: 500,
};

export interface Treasure {
  readonly id: number;
  readonly x: number;
  /** 浜からの距離（m）。0 が浜。 */
  readonly y: number;
  readonly tier: Tier;
}

/** 波の生成情報。実際の位置はシミュレーション側で時間積分する。 */
export interface SwellSeed {
  readonly id: number;
  /** この時刻に沖の端から入ってくる。 */
  readonly releaseTime: number;
  /** 波高（m）。水深がこの 1.35 倍を下回ると崩れて白波になる。 */
  readonly amp: number;
  /** セット内の何番目か。演出とチュートリアル用。 */
  readonly indexInSet: number;
}

/**
 * 生き物。避けるものと頼るものを混ぜて、海を生きた場所にする。
 *
 * 位置は時刻の純関数として揺らす（状態を持たない）。
 * 状態を持たせると保存すべきものが増え、決定論の検証面が広がってしまう。
 */
export type CreatureKind = 'jellyfish' | 'dolphin' | 'hatchling';

export interface Creature {
  readonly id: number;
  readonly kind: CreatureKind;
  /** 揺らぎの中心。 */
  readonly x: number;
  readonly y: number;
  /** 横揺れの幅（m）と位相。0 なら動かない。 */
  readonly drift: number;
  readonly phase: number;
}

/** その時刻における生き物の横位置。 */
export function creatureX(creature: Creature, t: number): number {
  if (creature.drift === 0) return creature.x;
  return creature.x + Math.sin(t * 0.6 + creature.phase) * creature.drift;
}

/** 離岸流の帯。沖へ引く。 */
export interface Rip {
  readonly xCenter: number;
  readonly halfWidth: number;
  /** 浜からの距離で、この区間に存在する。 */
  readonly yNear: number;
  readonly yFar: number;
}

export interface StageConfig {
  readonly id: string;
  readonly name: string;
  /** コース長（m）。スタート地点の沖からの距離。 */
  readonly length: number;
  /** 横方向の幅（m）。 */
  readonly laneWidth: number;
  readonly maxDepth: number;
  /** タイムボーナスの基準タイム（秒）。 */
  readonly baseTime: number;
  readonly difficulty: number;
  /** セットの周期（秒）とセット内の波数・間隔。 */
  readonly setPeriod: number;
  readonly setWaves: readonly [number, number];
  readonly waveGap: number;
  readonly ampRange: readonly [number, number];
  readonly ripCount: number;
  readonly treasures: Readonly<Record<Tier, number>>;
  /**
   * 沖のリーフ（浅瀬の盛り上がり）。ここで大きい波だけが早く割れ、
   * 内側のラグーンで崩れが戻り、岸際でもう一度割れる。
   * これがないと白波はコースの最後の数十メートルにしか存在せず、
   * ドルフィンスルーという看板メカニクスがほとんど発生しない。
   */
  readonly reef?: {
    /** 浜からの距離（m）。 */
    readonly y: number;
    /** 盛り上がりの高さ（m）。 */
    readonly height: number;
    /** 広がり（m）。 */
    readonly width: number;
  };
  /**
   * 干潮。走行が進むほど海全体が浅くなり、割れる位置が沖へ動く。
   * 時刻の純関数なので決定論は保たれる。
   */
  readonly tide?: {
    /** 最終的に下がる水深（m）。 */
    readonly drop: number;
    /** 下がりきるまでの時間（秒）。 */
    readonly overSeconds: number;
  };
  readonly creatures?: Readonly<Partial<Record<CreatureKind, number>>>;
  /** ランキング対象外。キッズモードだけが true。 */
  readonly unranked?: boolean;
}

/** ステージ 01 ワイキキ。ゆるいロングうねり。チュートリアルを兼ねる。 */
export const WAIKIKI: StageConfig = {
  id: 'waikiki',
  name: 'ワイキキ',
  length: 260,
  laneWidth: 30,
  maxDepth: 5.5,
  baseTime: 140,
  difficulty: 1.0,
  setPeriod: 15,
  setWaves: [3, 5],
  waveGap: 2.7,
  ampRange: [0.9, 1.9],
  ripCount: 1,
  treasures: { shell: 16, pearl: 6, coin: 3, relic: 1 },
  reef: { y: 170, height: 2.2, width: 30 },
  creatures: { jellyfish: 5, dolphin: 3, hatchling: 3 },
};

/**
 * ステージ 02 ノースショア。重いチューブと大波。
 * 新しく足す仕組みは **セットの周期** ひとつだけ。
 * リーフを浅くして大波が沖で割れるようにし、セットの合間に進む判断を作る。
 */
export const NORTH_SHORE: StageConfig = {
  id: 'northshore',
  name: 'ノースショア',
  length: 265,
  laneWidth: 30,
  maxDepth: 6.5,
  baseTime: 150,
  difficulty: 1.35,
  // セットが長く、来るときはまとめて来る。凪の見極めがそのままタイムになる。
  setPeriod: 22,
  setWaves: [4, 6],
  waveGap: 2.4,
  ampRange: [1.4, 2.8],
  ripCount: 2,
  treasures: { shell: 16, pearl: 7, coin: 4, relic: 1 },
  reef: { y: 180, height: 3.1, width: 34 },
  creatures: { jellyfish: 7, dolphin: 3, hatchling: 3 },
};

/**
 * ステージ 03 ウルワツ。速いリーフブレイク。
 * 新しく足す仕組みは **干潮**。進むほどリーフが浅くなり、
 * 後半ほど沖から割れる。前半に稼ぐか、後半の荒れを覚悟するかの判断になる。
 */
export const ULUWATU: StageConfig = {
  id: 'uluwatu',
  name: 'ウルワツ',
  length: 250,
  laneWidth: 28,
  maxDepth: 5.8,
  baseTime: 140,
  difficulty: 1.5,
  setPeriod: 13,
  setWaves: [3, 5],
  waveGap: 2.1,
  ampRange: [1.2, 2.3],
  ripCount: 2,
  treasures: { shell: 15, pearl: 7, coin: 4, relic: 1 },
  reef: { y: 135, height: 2.6, width: 22 },
  tide: { drop: 1.1, overSeconds: 110 },
  creatures: { jellyfish: 8, dolphin: 2, hatchling: 3 },
};

/** 遊べるステージ。無料範囲はこの 3 本（→ docs/03-monetization.md）。 */
export const STAGES: readonly StageConfig[] = [WAIKIKI, NORTH_SHORE, ULUWATU];

export function stageById(id: string): StageConfig {
  return STAGES.find((stage) => stage.id === id) ?? WAIKIKI;
}

/**
 * キッズモード。波を弱く、干潮なし、クラゲなし。
 * **ランキング対象外にすることを型で持たせる**。
 * 優しくしたステージのタイムが同じ表に載ると、ランキングの信頼が崩れる。
 */
export function toKidsMode(stage: StageConfig): StageConfig {
  return {
    ...stage,
    id: `${stage.id}-kids`,
    name: `${stage.name}（キッズ）`,
    ampRange: [stage.ampRange[0] * 0.55, stage.ampRange[1] * 0.6],
    setPeriod: stage.setPeriod * 1.4,
    ripCount: 0,
    tide: undefined,
    creatures: { ...stage.creatures, jellyfish: 0 },
    unranked: true,
  };
}

export interface Course {
  readonly stage: StageConfig;
  readonly seed: number;
  readonly swells: readonly SwellSeed[];
  readonly treasures: readonly Treasure[];
  readonly rips: readonly Rip[];
  readonly creatures: readonly Creature[];
}

/**
 * 浜に近づくほど浅くなる海底。砕波の位置と波の速度を決める。
 * @param elapsed 走行の経過時間（秒）。干潮のあるステージだけが使う。
 */
export function depthAt(stage: StageConfig, y: number, elapsed = 0): number {
  const t = Math.max(0, Math.min(1, y / stage.length));
  let depth = stage.maxDepth * Math.pow(t, 0.62);
  if (stage.reef) {
    // リーフの盛り上がりを引く。ここだけ局所的に浅くなり、大きい波が先に割れる。
    const d = (y - stage.reef.y) / stage.reef.width;
    depth -= stage.reef.height * Math.exp(-d * d);
  }
  if (stage.tide) {
    const progress = Math.max(0, Math.min(1, elapsed / stage.tide.overSeconds));
    depth -= stage.tide.drop * progress;
  }
  return Math.max(0.35, depth);
}

/** 浅水波の位相速度。浅いところでは波が遅くなり、追いつかれ方が変わる。 */
export function swellSpeed(stage: StageConfig, y: number, elapsed = 0): number {
  return Math.sqrt(GRAVITY * depthAt(stage, y, elapsed));
}

/**
 * 崩れた波（段波）が進む速度。浅水波の位相速度に下限を掛ける。
 * 下限がないと浜の近くで泳者とほぼ同速になり、衝撃帯が居座って避けられなくなる。
 */
export function foamSpeed(stage: StageConfig, y: number, elapsed = 0): number {
  return Math.max(swellSpeed(stage, y, elapsed), TUNING.foamMinSpeed);
}

/** その波高が崩れ始める水深。 */
export function breakDepth(amp: number): number {
  return amp * BREAK_DEPTH_RATIO;
}

/** その波高が掘れ上がり始める水深。崩れる予兆を出すための閾値。 */
export function crestDepth(amp: number): number {
  return amp * CREST_DEPTH_RATIO;
}

/**
 * いちど崩れた波が形を取り戻す水深。
 * 崩れたままにするとリーフで割れた波が内側の 200 m を泡で埋めてしまうため、
 * 深いところへ戻れば復活させる。ヒステリシスを持たせて境界でのちらつきを防ぐ。
 */
export function reformDepth(amp: number): number {
  return amp * REFORM_DEPTH_RATIO;
}

/**
 * シードからコースを生成する。
 * 乱数の消費順が固定されている必要があるため、生成の順番は変更しない。
 */
export function buildCourse(stage: StageConfig, seed: number, horizonSeconds = 260): Course {
  const rand = new Rand(seed);

  const swells: SwellSeed[] = [];
  let swellId = 0;
  let setStart = 3.0; // 開始直後は必ず凪。操作を覚える時間を与える。

  while (setStart < horizonSeconds) {
    const count = rand.int(stage.setWaves[0], stage.setWaves[1]);
    for (let i = 0; i < count; i++) {
      // セット内で波高が一度盛り上がって収まる。実際のセットの形を真似る。
      const envelope = Math.sin(((i + 1) / (count + 1)) * Math.PI);
      const amp =
        stage.ampRange[0] +
        (stage.ampRange[1] - stage.ampRange[0]) * envelope * rand.range(0.82, 1.0);
      swells.push({
        id: swellId++,
        releaseTime: setStart + i * stage.waveGap * rand.range(0.9, 1.1),
        amp,
        indexInSet: i,
      });
    }
    // セット間の凪。ここが「今のうちに進む」区間になる。
    setStart += stage.setPeriod * rand.range(0.85, 1.15);
  }

  const rips: Rip[] = [];
  for (let i = 0; i < stage.ripCount; i++) {
    const halfWidth = rand.range(2.2, 3.4);
    const xCenter = rand.range(halfWidth + 2, stage.laneWidth - halfWidth - 2);
    const yNear = rand.range(stage.length * 0.28, stage.length * 0.42);
    rips.push({
      xCenter,
      halfWidth,
      yNear,
      yFar: yNear + rand.range(stage.length * 0.18, stage.length * 0.28),
    });
  }

  const treasures: Treasure[] = [];
  let treasureId = 0;

  /** 最短ラインはコース中央。等級が上がるほど中央から遠い、または離岸流の中に置く。 */
  const placeOffLine = (offsetRatio: number): number => {
    const half = stage.laneWidth / 2;
    const side = rand.unit() < 0.5 ? -1 : 1;
    const offset = half * offsetRatio * rand.range(0.8, 1.0);
    return Math.max(1, Math.min(stage.laneWidth - 1, half + side * offset));
  };

  for (let i = 0; i < stage.treasures.shell; i++) {
    treasures.push({
      id: treasureId++,
      x: placeOffLine(rand.range(0.1, 0.55)),
      y: rand.range(stage.length * 0.05, stage.length * 0.96),
      tier: 'shell',
    });
  }
  for (let i = 0; i < stage.treasures.pearl; i++) {
    treasures.push({
      id: treasureId++,
      x: placeOffLine(rand.range(0.6, 0.85)),
      y: rand.range(stage.length * 0.1, stage.length * 0.92),
      tier: 'pearl',
    });
  }
  // 金貨は離岸流の中。取りに行くと沖へ引かれるので、タイムと正面から交換になる。
  for (let i = 0; i < stage.treasures.coin; i++) {
    const rip = rips.length > 0 ? rand.pick(rips) : null;
    if (rip) {
      treasures.push({
        id: treasureId++,
        x: rip.xCenter + rand.range(-rip.halfWidth * 0.6, rip.halfWidth * 0.6),
        y: rand.range(rip.yNear + 4, rip.yFar - 4),
        tier: 'coin',
      });
    } else {
      treasures.push({
        id: treasureId++,
        x: placeOffLine(0.9),
        y: rand.range(stage.length * 0.2, stage.length * 0.8),
        tier: 'coin',
      });
    }
  }
  // 遺物は 1 つだけ。砕波帯のいちばん外れ、浜の直前に置く。
  for (let i = 0; i < stage.treasures.relic; i++) {
    treasures.push({
      id: treasureId++,
      x: placeOffLine(0.95),
      y: rand.range(stage.length * 0.06, stage.length * 0.14),
      tier: 'relic',
    });
  }

  // ── 生き物 ──
  const creatures: Creature[] = [];
  let creatureId = 0;
  const counts = stage.creatures ?? {};

  // クラゲは最短ラインの上にも置く。避ける操作そのものが目的なので、
  // 宝と違って「寄り道の先」ではなく「通り道」に居てもらう必要がある。
  for (let i = 0; i < (counts.jellyfish ?? 0); i++) {
    creatures.push({
      id: creatureId++,
      kind: 'jellyfish',
      x: rand.range(2, stage.laneWidth - 2),
      y: rand.range(stage.length * 0.12, stage.length * 0.9),
      drift: rand.range(0.8, 2.4),
      phase: rand.range(0, Math.PI * 2),
    });
  }
  // イルカは寄り道の先。掴まえに行く価値があるだけ離す。
  for (let i = 0; i < (counts.dolphin ?? 0); i++) {
    creatures.push({
      id: creatureId++,
      kind: 'dolphin',
      x: rand.range(3, stage.laneWidth - 3),
      y: rand.range(stage.length * 0.2, stage.length * 0.88),
      drift: rand.range(1.5, 3.5),
      phase: rand.range(0, Math.PI * 2),
    });
  }
  // 子ガメは動かない。拾って浜まで運ぶ対象なので、探して見つかることが大事。
  for (let i = 0; i < (counts.hatchling ?? 0); i++) {
    creatures.push({
      id: creatureId++,
      kind: 'hatchling',
      x: rand.range(2, stage.laneWidth - 2),
      y: rand.range(stage.length * 0.15, stage.length * 0.85),
      drift: 0,
      phase: 0,
    });
  }

  // シミュレーションは releaseTime 昇順を前提にカーソルで投入していくため、ここで確定させる。
  swells.sort((a, b) => a.releaseTime - b.releaseTime || a.id - b.id);

  return { stage, seed, swells, treasures, rips, creatures };
}

/** そのコースで理論上取り切れる最大の宝ポイント。UI の達成率表示に使う。 */
export function maxTreasurePoints(course: Course): number {
  return course.treasures.reduce((sum, t) => sum + TREASURE_POINTS[t.tier], 0);
}
